import { KeyboardEventContextProvider } from "providers/keyboard-event-provider/KeyboardEventProvider";
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "store";
import { ResizablePanels } from "@sandbox/resizable-panels";
import { Placeholder } from "../placeholder/Placeholder";
import { Editor } from "./editor/Editor";
import styles from "./Playground.module.css";
import { Transpiler } from "./transpiler/Transpiler";
import { Visualizer } from "./visualizer/Visualizer";
import { VisualizerHeader } from "./visualizer/VisualizerHeader";
import { TrackedSandboxEvent, trackComponent } from "services/telemetry-service/telemetry";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useTimer } from "hooks/useTimer";
import { useLocation } from "react-router-dom";

const Playground = () => {
  const ref = useRef<HTMLIFrameElement>(null);
  const sandboxMetadata = useAppSelector((state) => state.activeSandbox.metadata);
  const layout = useAppSelector((state) => state.settings.layout);
  const [editorVisible, setEditorVisible] = useState(layout.editorPanel !== "closed");
  const [visualizerVisible, setVisualizerVisible] = useState(layout.previewPanel !== "closed");

  const appInsights = useAppInsightsContext();
  const timer = useTimer();
  const location = useLocation();

  timer.start();

  // Tracking duration the sandbox was opened to AppInsights
  useEffect(() => {
    return () => {
      if (!sandboxMetadata)
        return;

      trackComponent<TrackedSandboxEvent>(
        appInsights,
        "Sandbox",
        timer.end(),
        location.pathname,
        { SandboxName: sandboxMetadata.name, SandboxUser: sandboxMetadata.username, SandboxUserId: sandboxMetadata.userId, SandboxId: sandboxMetadata.id });
    }
  }, [sandboxMetadata?.id, location])

  const editorWidth = layout.editorPanelSize ?? 50;

  const editorPanel = <Editor expanded={!visualizerVisible} onReset={() => setVisualizerVisible(true)} />
  const previewPanel = (
    <div className={styles.visualizer} data-testid="atp-preview-panel">
      {layout.toolbars !== false
        ? <VisualizerHeader
          expanded={!editorVisible}
          onExpand={() => setEditorVisible(false)}
          onCollapse={() => setVisualizerVisible(false)}
          onReset={() => setEditorVisible(true)} />
        : <div></div>}
      <Visualizer ref={ref} />
    </div>
  );

  if (!sandboxMetadata)
    return <Placeholder loading={true} />

  // Show only preview panel
  if (layout.editorPanel === "hidden")
    return (
      <KeyboardEventContextProvider>
        <Transpiler iframeRef={ref}>{previewPanel}</Transpiler>
      </KeyboardEventContextProvider>
    );

  // Show only editor panel
  if (layout.previewPanel === "hidden") {
    return (
      <KeyboardEventContextProvider>
        <Transpiler iframeRef={ref}>
          {editorPanel}
        </Transpiler>
      </KeyboardEventContextProvider>
    );
  }

  // Show both editor and preview panels in resizable split view
  return (
    <KeyboardEventContextProvider>
      <Transpiler iframeRef={ref}>
        <ResizablePanels
          snap={true}
          hideSashOnSnap={true}
          defaultSizes={[editorWidth, 100 - editorWidth]}
          firstPanel={{
            visible: editorVisible,
            preferredSize: `${editorWidth}%`,
            onVisibilityChange: setEditorVisible,
            minSize: 200,
          }}
          secondPanel={{
            visible: visualizerVisible,
            preferredSize: `${100 - editorWidth}%`,
            onVisibilityChange: setVisualizerVisible,
            minSize: 200,
          }}
        >
          {editorPanel}
          {previewPanel}
        </ResizablePanels>
      </Transpiler>
    </KeyboardEventContextProvider >
  )
}

export default Playground;